import type {
  AgencyStats,
  AmaEvent,
  GameEventWithProgress,
  LeaderboardData,
  StoreOrder,
  StoreProduct,
} from "@/contexts/auth-api/dashboard-types"
import { dashboardTreatyClient } from "@/contexts/auth-api/treaty-client"

interface DashboardApiDeps {
  getToken: () => string | null
}

interface TreatyResponse<T = unknown> {
  data?: T | null
  error?: {
    value?: unknown
  } | null
}

type LeaderboardMode = "agency" | "agent"

interface ActiveSlotSettings {
  outcomeTypes: Array<{ name: string; probability: number; enabled: boolean }>
  outcomeRanges: Record<string, Array<{ value: number | string; probability: number; label: string; metadata?: Record<string, unknown> }>>
  source: string
  expiresAt?: string
}

interface MockStoreCheckoutPayload {
  items: Array<{ productId: string; quantity: number }>
  shipping: {
    fullName: string
    email: string
    phone?: string
    addressLine1: string
    addressLine2?: string
    city: string
    state: string
    postalCode: string
    country: string
  }
  notes?: string
}

interface PendingAirdrop {
  _id: string
  currency: "cash" | "points"
  amount: number
  note: string
  claimDeadline: string | null
  createdAt: string
}

interface ClaimedAirdrop {
  currency: "cash" | "points"
  amount: number
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function extractTreatyPayload<T extends { success: boolean; message?: string }>(value: unknown): T | null {
  if (!isRecord(value)) return null

  const queue: unknown[] = [value]
  const seen = new Set<object>()

  while (queue.length > 0) {
    const current = queue.shift()
    if (!isRecord(current) || seen.has(current)) continue
    seen.add(current)

    if (typeof current.success === "boolean") {
      return current as T
    }

    for (const [key, nested] of Object.entries(current)) {
      if (!nested || typeof nested !== "object") continue

      if (
        key === "data" ||
        key === "error" ||
        key === "value" ||
        key === "response" ||
        /^[1-5]\d\d$/.test(key)
      ) {
        queue.unshift(nested)
      } else {
        queue.push(nested)
      }
    }
  }

  return null
}

function unwrapTreatyResult<T extends { success: boolean; message?: string }>(
  response: TreatyResponse<unknown>,
  fallbackMessage: string
): T {
  const payload = extractTreatyPayload<T>(response.data) ?? extractTreatyPayload<T>(response.error?.value)
  if (payload) return payload
  return { success: false, message: fallbackMessage } as T
}

function getAuthHeaders(token: string) {
  return { authorization: `Bearer ${token}` }
}

export function createDashboardApi(deps: DashboardApiDeps) {
  const getActiveSlotSettings = async (): Promise<{ success: boolean; message?: string; settings?: ActiveSlotSettings }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await dashboardTreatyClient.api["slot-settings"].active.get({
        headers: getAuthHeaders(token),
      })
      const data = unwrapTreatyResult<{ success: boolean; message?: string; settings?: ActiveSlotSettings }>(
        response,
        "Failed to get active slot settings"
      )
      return { success: Boolean(data.success), message: data.message, settings: data.settings }
    } catch (error) {
      console.error("Get active slot settings error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const getLeaderboard = async (
    mode?: LeaderboardMode
  ): Promise<{ success: boolean; data?: LeaderboardData; message?: string }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await dashboardTreatyClient.api.leaderboard.get({
        headers: getAuthHeaders(token),
        query: mode ? { mode } : undefined,
      })
      const data = unwrapTreatyResult<{
        success: boolean
        leaderboard?: LeaderboardData["leaderboard"]
        currentUserPosition?: LeaderboardData["currentUserPosition"]
        mode?: LeaderboardData["mode"]
        message?: string
      }>(response, "Failed to get leaderboard")

      if (data.success) {
        return {
          success: true,
          data: {
            leaderboard: Array.isArray(data.leaderboard) ? data.leaderboard : [],
            currentUserPosition: data.currentUserPosition ?? null,
            mode: data.mode === "agency" || data.mode === "agent" ? data.mode : (mode || "agent"),
          },
        }
      }

      return { success: false, message: data.message || "Failed to get leaderboard" }
    } catch (error) {
      console.error("Get leaderboard error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const getAgencyStats = async (agencyId: string): Promise<{ success: boolean; stats?: AgencyStats; message?: string }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await dashboardTreatyClient.api.agency.stats.get({
        headers: getAuthHeaders(token),
        query: { agencyId },
      })
      const data = unwrapTreatyResult<{ success: boolean; stats?: AgencyStats; message?: string }>(
        response,
        "Failed to get agency stats"
      )

      if (data.success) {
        return { success: true, stats: data.stats }
      }

      return { success: false, message: data.message || "Failed to get agency stats" }
    } catch (error) {
      console.error("Get agency stats error:", error)
      return { success: false, message: "Network error. Please try again." }
    }
  }

  const getAmaEvents = async (): Promise<{ success: boolean; events?: AmaEvent[]; message?: string }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await dashboardTreatyClient.api.events.get({
        headers: getAuthHeaders(token),
      })
      const data = unwrapTreatyResult<{ success: boolean; events?: AmaEvent[]; message?: string }>(response, "Network error")
      return { success: data.success, events: data.events, message: data.message }
    } catch (error) {
      console.error("Get AMA events error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getStoreProducts = async (): Promise<{ success: boolean; products?: StoreProduct[]; message?: string }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await dashboardTreatyClient.api.store.products.get({
        headers: getAuthHeaders(token),
      })
      const data = unwrapTreatyResult<{ success: boolean; products?: StoreProduct[]; message?: string }>(response, "Network error")
      return { success: data.success, products: data.products, message: data.message }
    } catch (error) {
      console.error("Get store products error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getMyStoreOrders = async (): Promise<{ success: boolean; orders?: StoreOrder[]; message?: string }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await dashboardTreatyClient.api.store.orders.get({
        headers: getAuthHeaders(token),
      })
      const data = unwrapTreatyResult<{ success: boolean; orders?: StoreOrder[]; message?: string }>(response, "Network error")
      return { success: data.success, orders: data.orders, message: data.message }
    } catch (error) {
      console.error("Get my store orders error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const mockStoreCheckout = async (
    payload: MockStoreCheckoutPayload
  ): Promise<{ success: boolean; order?: StoreOrder; message?: string }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await dashboardTreatyClient.api.store.checkout.mock.post(payload, {
        headers: getAuthHeaders(token),
      })
      const data = unwrapTreatyResult<{ success: boolean; order?: StoreOrder; message?: string }>(response, "Network error")
      return { success: data.success, order: data.order, message: data.message }
    } catch (error) {
      console.error("Mock store checkout error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getGameEvents = async (): Promise<{
    success: boolean
    events?: GameEventWithProgress[]
    featuredEvent?: GameEventWithProgress | null
    message?: string
  }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await dashboardTreatyClient.api["game-events"].get({
        headers: getAuthHeaders(token),
      })
      return unwrapTreatyResult<{
        success: boolean
        events?: GameEventWithProgress[]
        featuredEvent?: GameEventWithProgress | null
        message?: string
      }>(response, "Network error")
    } catch (error) {
      console.error("Get game events error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getGameEventDetail = async (
    eventId: string
  ): Promise<{ success: boolean; event?: GameEventWithProgress; message?: string }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await dashboardTreatyClient.api["game-events"]({ eventId }).get({
        headers: getAuthHeaders(token),
      })
      return unwrapTreatyResult<{ success: boolean; event?: GameEventWithProgress; message?: string }>(
        response,
        "Network error"
      )
    } catch (error) {
      console.error("Get game event detail error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const getUserPendingAirdrops = async (): Promise<{
    success: boolean
    message?: string
    airdrops?: PendingAirdrop[]
  }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await dashboardTreatyClient.api.airdrops.pending.get({
        headers: getAuthHeaders(token),
      })
      const data = unwrapTreatyResult<{ success: boolean; message?: string; airdrops?: PendingAirdrop[] }>(
        response,
        "Unexpected response"
      )
      return { success: data.success, message: data.message, airdrops: data.airdrops }
    } catch (error) {
      console.error("Get pending airdrops error:", error)
      return { success: false, message: "Network error" }
    }
  }

  const claimAirdrop = async (
    airdropId: string
  ): Promise<{ success: boolean; message?: string; claimed?: ClaimedAirdrop }> => {
    const token = deps.getToken()
    if (!token) return { success: false, message: "Not authenticated" }

    try {
      const response = await dashboardTreatyClient.api.airdrops.claim.post(
        { airdropId },
        { headers: getAuthHeaders(token) }
      )
      return unwrapTreatyResult<{ success: boolean; message?: string; claimed?: ClaimedAirdrop }>(
        response,
        "Network error"
      )
    } catch (error) {
      console.error("Claim airdrop error:", error)
      return { success: false, message: "Network error" }
    }
  }

  return {
    getActiveSlotSettings,
    getLeaderboard,
    getAgencyStats,
    getAmaEvents,
    getStoreProducts,
    getMyStoreOrders,
    mockStoreCheckout,
    getGameEvents,
    getGameEventDetail,
    getUserPendingAirdrops,
    claimAirdrop,
  }
}
